import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import ArchiveSvg from "@redotech/redo-icons/archive.svg";
import CheckSvg from "@redotech/redo-icons/check_filled.svg";
import DotsHorizontalSvg from "@redotech/redo-icons/dots-horizontal.svg";
import StatusSvg from "@redotech/redo-icons/status.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Flex } from "@redotech/redo-web/flex";
import EnvelopeIcon from "@redotech/redo-web/icon-old/mail.svg";
import UndoArrow from "@redotech/redo-web/icon-old/undo-arrow.svg";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo, useState } from "react";
import {
  useConversationActionToolbar,
  type ActionMenuItem,
  type ConversationActionId,
} from "../hooks/use-conversation-action-toolbar";

export interface ConversationActionToolbarProps {
  conversationId: ConversationId;
  status: ConversationStatus;
  disabled?: boolean;
}

const ACTION_ICONS: Record<ConversationActionId, React.ComponentType> = {
  markOpen: UndoArrow,
  markInProgress: StatusSvg,
  markUnread: EnvelopeIcon,
  archive: ArchiveSvg,
  close: CheckSvg,
  reopen: UndoArrow,
};

export const ConversationActionToolbar = memo(
  function ConversationActionToolbar({
    conversationId,
    status,
    disabled = false,
  }: ConversationActionToolbarProps) {
    const {
      closeReopenAction,
      menuItems,
      isLoading,
      canCloseConversation,
      handleCloseReopen,
      handleMenuAction,
    } = useConversationActionToolbar(conversationId, status);

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    const isDisabled = disabled || isLoading;

    const commandMenuItems = useMemo(
      (): RedoCommandMenuItem[] =>
        menuItems.map((item: ActionMenuItem) => ({
          Icon: ACTION_ICONS[item.id],
          text: item.text,
          onClick: () => handleMenuAction(item.id),
          tooltip: item.tooltip,
          disabled: item.disabled,
        })),
      [menuItems, handleMenuAction],
    );

    const CloseReopenIcon =
      closeReopenAction.id === "close" ? CheckSvg : UndoArrow;

    return (
      <Flex align="center" gap="sm">
        {commandMenuItems.length > 0 && (
          <>
            <RedoButton
              disabled={isDisabled}
              hierarchy="secondary"
              IconLeading={DotsHorizontalSvg}
              onClick={() => setMenuOpen(!menuOpen)}
              ref={setMenuAnchor}
              size="sm"
            />
            <RedoCommandMenu
              anchor={menuAnchor}
              items={commandMenuItems}
              open={menuOpen}
              setOpen={setMenuOpen}
            />
          </>
        )}
        {canCloseConversation && (
          <Tooltip arrow title={closeReopenAction.tooltip}>
            <span>
              <RedoButton
                disabled={isDisabled}
                hierarchy="primary"
                IconLeading={CloseReopenIcon}
                onClick={handleCloseReopen}
                size="sm"
                text={closeReopenAction.text}
              />
            </span>
          </Tooltip>
        )}
      </Flex>
    );
  },
);
